import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type {} from '@redux-devtools/extension'
import type {
  RequirementCriteriaLink,
  RequirementsCoverageStats,
  EvaluationCoverageStats,
  LinkSource,
} from '@/lib/schemas'
import { api } from '@/lib/api'

// ===================
// Types
// ===================

interface RequirementCriteriaLinksState {
  // State
  links: RequirementCriteriaLink[]
  isLoading: boolean
  error: string | null
  currentBidId: string | null

  // Coverage stats
  requirementsCoverage: RequirementsCoverageStats | null
  evaluationCoverage: EvaluationCoverageStats | null
  isFetchingCoverage: boolean

  // Actions - Fetching
  fetchLinks: (bidId: string) => Promise<void>
  fetchRequirementsCoverage: (bidId: string) => Promise<void>
  fetchEvaluationCoverage: (bidId: string) => Promise<void>
  fetchAllCoverageStats: (bidId: string) => Promise<void>

  // Actions - CRUD
  createLink: (
    bidId: string,
    requirementId: string,
    evaluationCriterionId: string,
    linkSource?: LinkSource
  ) => Promise<RequirementCriteriaLink>
  deleteLink: (id: string) => Promise<void>

  // Actions - Utility
  clearError: () => void
  reset: () => void

  // Computed
  getLinksForRequirement: (requirementId: string) => RequirementCriteriaLink[]
  getLinksForCriterion: (criterionId: string) => RequirementCriteriaLink[]
  getLinkById: (id: string) => RequirementCriteriaLink | undefined
  isRequirementLinked: (requirementId: string) => boolean
  isCriterionLinked: (criterionId: string) => boolean
  getLinkedCriteriaIds: (requirementId: string) => string[]
  getLinkedRequirementIds: (criterionId: string) => string[]
}

export const useRequirementCriteriaLinksStore = create<RequirementCriteriaLinksState>()(
  devtools(
    (set, get) => ({
      // Initial state
      links: [],
      isLoading: false,
      error: null,
      currentBidId: null,
      requirementsCoverage: null,
      evaluationCoverage: null,
      isFetchingCoverage: false,

      // Fetch links for a bid
      fetchLinks: async (bidId: string) => {
        try {
          set({ isLoading: true, error: null, currentBidId: bidId })
          const links = await api.get<RequirementCriteriaLink[]>(
            `/bids/${bidId}/requirement-criteria-links`
          )
          set({ links, isLoading: false })
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch requirement-criteria links',
            isLoading: false,
          })
        }
      },

      // Fetch requirements coverage stats
      fetchRequirementsCoverage: async (bidId: string) => {
        try {
          set({ isFetchingCoverage: true, error: null })
          const requirementsCoverage = await api.get<RequirementsCoverageStats>(
            `/bids/${bidId}/requirements/coverage-stats`
          )
          set({ requirementsCoverage, isFetchingCoverage: false })
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch requirements coverage',
            isFetchingCoverage: false,
          })
        }
      },

      // Fetch evaluation coverage stats
      fetchEvaluationCoverage: async (bidId: string) => {
        try {
          set({ isFetchingCoverage: true, error: null })
          const evaluationCoverage = await api.get<EvaluationCoverageStats>(
            `/bids/${bidId}/evaluation-criteria/coverage-stats`
          )
          set({ evaluationCoverage, isFetchingCoverage: false })
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch evaluation coverage',
            isFetchingCoverage: false,
          })
        }
      },

      // Fetch all coverage stats
      fetchAllCoverageStats: async (bidId: string) => {
        try {
          set({ isFetchingCoverage: true, error: null })
          const [requirementsCoverage, evaluationCoverage] = await Promise.all([
            api.get<RequirementsCoverageStats>(`/bids/${bidId}/requirements/coverage-stats`),
            api.get<EvaluationCoverageStats>(`/bids/${bidId}/evaluation-criteria/coverage-stats`),
          ])
          set({ requirementsCoverage, evaluationCoverage, isFetchingCoverage: false })
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch coverage stats',
            isFetchingCoverage: false,
          })
        }
      },

      // Create a link
      createLink: async (
        bidId: string,
        requirementId: string,
        evaluationCriterionId: string,
        linkSource: LinkSource = 'manual'
      ) => {
        try {
          set({ error: null })
          const link = await api.post<RequirementCriteriaLink>(
            `/bids/${bidId}/requirement-criteria-links`,
            {
              requirementId,
              evaluationCriterionId,
              linkSource,
            }
          )
          set((state) => ({
            links: [...state.links, link],
          }))
          return link
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to create link',
          })
          throw error
        }
      },

      // Delete a link
      deleteLink: async (id: string) => {
        try {
          set({ error: null })
          await api.delete(`/requirement-criteria-links/${id}`)
          set((state) => ({
            links: state.links.filter((l) => l.id !== id),
          }))
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to delete link',
          })
          throw error
        }
      },

      // Clear error
      clearError: () => {
        set({ error: null })
      },

      // Reset store state
      reset: () => {
        set({
          links: [],
          isLoading: false,
          error: null,
          currentBidId: null,
          requirementsCoverage: null,
          evaluationCoverage: null,
          isFetchingCoverage: false,
        })
      },

      // Get links for a requirement
      getLinksForRequirement: (requirementId: string) => {
        return get().links.filter((l) => l.requirementId === requirementId)
      },

      // Get links for a criterion
      getLinksForCriterion: (criterionId: string) => {
        return get().links.filter((l) => l.evaluationCriterionId === criterionId)
      },

      // Get link by ID
      getLinkById: (id: string) => {
        return get().links.find((l) => l.id === id)
      },

      // Check if requirement is linked
      isRequirementLinked: (requirementId: string) => {
        return get().links.some((l) => l.requirementId === requirementId)
      },

      // Check if criterion is linked
      isCriterionLinked: (criterionId: string) => {
        return get().links.some((l) => l.evaluationCriterionId === criterionId)
      },

      // Get linked criteria IDs for a requirement
      getLinkedCriteriaIds: (requirementId: string) => {
        return get()
          .links.filter((l) => l.requirementId === requirementId)
          .map((l) => l.evaluationCriterionId)
      },

      // Get linked requirement IDs for a criterion
      getLinkedRequirementIds: (criterionId: string) => {
        return get()
          .links.filter((l) => l.evaluationCriterionId === criterionId)
          .map((l) => l.requirementId)
      },
    }),
    { name: 'requirement-criteria-links-store' }
  )
)
